import React, { useCallback, useMemo, useRef, useState } from "react";
import { STORM_FILE_IMAGE_TYPES, STORM_FILE_VIDEO_TYPES } from "theme/config/constants";

import Div from "theme/layouts/Div";
import MediaGallerySlider from "theme/components/sliders/MediaGallery";
import PropTypes from "prop-types";
import ThemeBadge from "theme/components/badges/ThemeBadge";
import ThumbnailWrapper from "theme/components/sliders/MediaGallery/ThumbnailWrapper";
import checkActivation from "core-web/utils/checkActivation";
import discountInPercentage from "core-web/utils/discountInPercentage";
import { getMediaUrl } from "core-web/utils/getMediaUrl";
import { inServer } from "core-web/constants";
import styled from "theme/libs/styled";
import { useSelector } from "react-redux";

const SliderColumn = styled("div")`
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;

    &[data-has-thumbnails="true"] {
        padding-bottom: 16px;

        ${({ theme }) => theme.above.mobileLg} {
            padding-bottom: 24px;
        }
    }

    ${({ theme }) => theme.above.mobileLg} {
        padding-top: 0;
    }

    ${({ theme }) => theme.above.tabletLg} {
        left: -16px;
    }
`;

const SliderBox = styled("div")`
    position: relative;
    width: 100%;

    ${({ theme }) => theme.above.tabletLg} {
        position: sticky;
        margin-left: 16px;
        border-radius: 0 0 8px 8px;
        transition: top ${({ theme }) => theme.transitions.headerAnimation};
        top: ${({ theme }) => theme.variables.headerHeight + 8}px;
    }
`;

const BadgeWrapper = styled("div")`
    position: absolute;
    display: flex;
    flex-direction: row;
    gap: 8px;
    top: 16px;
    left: 16px;
    z-index: 1;

    ${({ theme }) => theme.above.mobileLg} {
        top: 24px;
        left: 24px;
    }
`;

const useSlider = () => {
    const ref = useRef(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const setRef = useCallback((node) => (ref.current = node), []);
    const props = { ref: setRef, onSlideChange: setActiveIndex };
    const iframes = inServer ? [] : document.querySelectorAll(".ifrm");
    const video = inServer ? [] : document.querySelectorAll(".vid");

    const stopVideos = () => {
        iframes.forEach((iframe) => {
            //Pauses yt-video when user changes slide without adding to history-stash. Only works with "?enablejsapi=1"-suffix on the url-src.
            iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', "*");
        });
        video.forEach((v) => {
            v.pause();
        });
    };
    if (iframes.length || video.length) {
        stopVideos();
    }

    const isThumbnailActive = (index, slidesPerView, numberOfSlides) => {
        return (
            (activeIndex === null && index === 0) ||
            activeIndex === index ||
            (activeIndex === -1 && index === numberOfSlides - 1) ||
            (activeIndex === -2 && index === numberOfSlides - 2) ||
            (slidesPerView === 2 &&
                (activeIndex === index - 1 ||
                    (activeIndex === numberOfSlides - 1 && index === 0) ||
                    (activeIndex === -1 && index === numberOfSlides) ||
                    (activeIndex === -2 && index === numberOfSlides - 1)))
        );
    };

    return [
        props,
        {
            activeIndex,
            isThumbnailActive,
            slidePrev: () => ref.current?.slidePrev(),
            slideNext: () => ref.current?.slideNext(),
            slideTo: (index) => ref.current?.slideTo(index),
        },
    ];
};

const MediaGallery = ({ product, selectedProduct, selectedProductVariants, ...rest }) => {
    const columnRef = useRef();
    const badgeRef = useRef();
    const lastVariantsArray = useRef([]);

    const { code: defaultPriceListCode } = useSelector(({ application }) => application?.defaultPriceList) || {};
    const priceListCode = selectedProduct?.price_list_code;
    const badges = useSelector(({ application }) => application.config.options.products?.badges) || {};
    const enabledFlags = badges?.enabled_flags || [];

    const [sliderProps, sliderOptions] = useSlider();
    const { activeIndex, isThumbnailActive, slideTo } = sliderOptions || {};

    const filteredProductFiles = product.files?.filter(({ code }) => code === "wallmek_extra_images") || [];
    const name = product.description_header || product.name;
    const image = selectedProduct.format.image;

    const getImagesOfAllProducts = () => {
        const files = [];
        const variantsArray = Object.values(selectedProductVariants).filter((i) => i) || [];

        if (variantsArray.length) {
            variantsArray.forEach((variant) => {
                if (product.id === variant.id) {
                    return;
                }

                const variantHasChanged = lastVariantsArray.current.every((v) => v.id !== variant.id);

                const productImage = {
                    id: variant.id,
                    image: variant.format?.image || variant.image,
                    trigger: !!variant.variant_name && !!variantHasChanged,
                };

                if (productImage.image) {
                    files.push(productImage);
                }
            });
        }

        lastVariantsArray.current = variantsArray;

        return files;
    };

    const extraFiles = getImagesOfAllProducts();
    const allFiles = [...filteredProductFiles, ...extraFiles];

    const medias = useMemo(
        () =>
            allFiles.reduce(
                (acc, cur, key) => {
                    const { type, name: title, format, path, code } = cur;

                    if (format?.image && STORM_FILE_IMAGE_TYPES.includes(type)) {
                        return [...acc, { key, title, code, image: getMediaUrl(format.image) }];
                    }

                    if (STORM_FILE_VIDEO_TYPES.includes(type)) {
                        if (path) {
                            return [...acc, { key, title, code, youtube: path }];
                        }
                        if (format.image) {
                            return [...acc, { key, title, code, video: getMediaUrl(format.image) }];
                        }
                    }

                    return acc;
                },
                [{ image: getMediaUrl(image), title: name, key: image }],
            ),
        [image, name, allFiles],
    );

    const flags = [...(product?.format?.flags || []), ...(selectedProduct.format.flags || [])];

    const filteredFlags = !!enabledFlags.length
        ? enabledFlags.filter(
              (enabledFlag) =>
                  flags.some((flag) => flag.id === enabledFlag.flag.id) &&
                  checkActivation(enabledFlag?.badge_activation),
          )
        : [];

    const hasThumbnails = medias?.length > 1;

    const discountPercentage = discountInPercentage(selectedProduct?.standardPriceExVat, selectedProduct?.priceExVat);
    let showDiscount = discountPercentage > 9.5;
    if (priceListCode && showDiscount) {
        showDiscount = priceListCode !== defaultPriceListCode;
    }

    return (
        <SliderColumn ref={columnRef} data-has-thumbnails={hasThumbnails} {...rest}>
            <SliderBox>
                <BadgeWrapper ref={badgeRef}>
                    {!!filteredFlags?.length &&
                        filteredFlags.map(({ flag, flag_name_overwrite: flagTranslation, badge_theme: badgeTheme }) => (
                            <ThemeBadge
                                key={flag.name}
                                badgeTheme={badgeTheme}
                                text={flagTranslation || flag.name}
                                textStyle="Myriad Pro/14-100"
                            />
                        ))}
                </BadgeWrapper>
                <Div>
                    <MediaGallerySlider
                        key={image}
                        image={image}
                        medias={medias}
                        sliderOptions={sliderOptions}
                        sliderProps={sliderProps}
                    />
                </Div>

                {hasThumbnails && (
                    <ThumbnailWrapper
                        activeIndex={activeIndex}
                        slideTo={slideTo}
                        isThumbnailActive={isThumbnailActive}
                        medias={medias}
                    />
                )}
            </SliderBox>
        </SliderColumn>
    );
};

MediaGallery.propTypes = {
    product: PropTypes.object.isRequired,
    selectedProduct: PropTypes.object,
    selectedProductVariants: PropTypes.object,
};

MediaGallery.defaultProps = {
    selectedProduct: {},
    selectedProductVariants: {},
};

export default MediaGallery;
